/**
 * Form Error Handler with Retry Logic
 * Handles form submission errors with retry mechanism, timeout handling, and network detection
 */

/**
 * Determines if an error is a network error (offline or connection issues)
 */
export function isNetworkError(error) {
  if (!error) return false;

  // Check if navigator indicates offline
  if (!navigator.onLine) return true;

  // Check common network error indicators
  if (
    error.code === 'ECONNREFUSED' ||
    error.code === 'ENOTFOUND' ||
    error.code === 'ETIMEDOUT' ||
    error.message?.includes('Network') ||
    error.message?.includes('Failed to fetch') ||
    error.message?.includes('timeout')
  ) {
    return true;
  }

  // Check for specific axios/HTTP error codes that indicate network issues
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
    return true;
  }

  return false;
}

/**
 * Get user-friendly error message based on error type
 */
export function getErrorMessage(error, context = 'operation') {
  if (!error) return `Failed to complete ${context}`;

  // Handle string errors
  if (typeof error === 'string') return error;

  // Handle error objects with message property
  if (error.message) {
    // Network errors
    if (isNetworkError(error)) {
      return 'Network connection lost. Please check your internet connection.';
    }

    // Validation errors
    if (error.code === 400 || error.message?.includes('validation')) {
      return error.message || `Invalid ${context} data`;
    }

    // Authentication errors
    if (error.code === 401) {
      return 'Your session has expired. Please log in again.';
    }

    // Permission errors
    if (error.code === 403) {
      return 'You do not have permission to perform this action.';
    }

    // Not found errors
    if (error.code === 404) {
      return `The requested ${context} was not found.`;
    }

    // Server errors
    if (error.code >= 500) {
      return 'Server error. Please try again later.';
    }

    return error.message;
  }

  return `Failed to complete ${context}`;
}

/**
 * Exponential backoff calculator
 * Returns wait time in ms for a given retry attempt
 */
export function getBackoffDelay(retryCount, baseDelay = 1000, maxDelay = 30000) {
  const delay = Math.min(baseDelay * Math.pow(2, retryCount), maxDelay);
  // Add jitter to prevent thundering herd
  const jitter = Math.random() * (delay * 0.1);
  return Math.floor(delay + jitter);
}

/**
 * Format retry message with backoff feedback.
 *
 * This is a plain JS util with no i18n context of its own, so callers
 * that can reach `t()` (from useI18n/useT) should pass it as the 4th
 * argument to get a localized string (keys live in src/i18n/ns/ui.js:
 * `ui.retryAttempt` and `ui.retryingIn`). Without a `t`, this falls
 * back to English -- only acceptable for non-UI consumers (logging,
 * tests); anything rendered to the user should supply `t`.
 */
export function getRetryMessage(retryCount, maxRetries, nextDelay = null, t = null) {
  const message = t
    ? t('ui.retryAttempt', { count: retryCount })
    : `Attempt ${retryCount}/${maxRetries}`;

  if (nextDelay !== null) {
    const seconds = Math.ceil(nextDelay / 1000);
    const suffix = t ? t('ui.retryingIn', { seconds }) : `Retrying in ${seconds}s...`;
    return `${message} - ${suffix}`;
  }

  return message;
}

/**
 * Create a form submission wrapper with retry logic
 *
 * By default the call is treated as NOT idempotent (a POST that creates something). Such a call
 * is re-sent only when the first attempt provably never reached the server: the browser is offline
 * or the API answered 429 before running the handler. A timeout, a 5xx or a dropped connection can
 * all happen after the row was written (the timed-out request is not cancelled either), so
 * re-sending them is how duplicate records get created: the error is thrown to the caller instead.
 * Pass `idempotent: true` for a call that is safe to repeat.
 */
export async function submitFormWithRetry(
  submitFn,
  options = {}
) {
  const {
    maxRetries = 3,
    timeout = 30000,
    onRetry = () => {},
    onTimeout = () => {},
    context = 'form submission',
    // Optional useI18n()/useT() translate function -- pass it so the
    // retry message handed to onRetry() is localized. See getRetryMessage.
    t = null,
    idempotent = false
  } = options;

  let lastError = null;
  let dynamicTimeout = timeout;

  for (let retryCount = 0; retryCount <= maxRetries; retryCount++) {
    try {
      const currentTimeout = dynamicTimeout;

      // Execute submission with timeout
      return await Promise.race([
        submitFn(retryCount),
        new Promise((_, reject) =>
          setTimeout(
            () => reject({ code: 'TIMEOUT', message: `${context} timed out after ${currentTimeout}ms` }),
            currentTimeout
          )
        )
      ]);
    } catch (error) {
      lastError = error;

      // Check if this is a timeout error
      if (error.code === 'TIMEOUT') {
        onTimeout?.(error, retryCount, maxRetries);

        // Don't retry timeouts beyond max retries, and never re-send a non-idempotent call: the
        // request that timed out is still running and may yet commit.
        if (!idempotent || retryCount >= maxRetries) {
          throw error;
        }

        // Increase timeout for next attempt
        dynamicTimeout = Math.min(dynamicTimeout * 1.5, 120000);
        continue;
      }

      // Network errors - always retry
      const isNetwork = isNetworkError(error);

      // If max retries exceeded and it's not a network error, throw
      if (retryCount >= maxRetries) {
        throw error;
      }

      // A call that is safe to repeat retries on network errors, 429 and 5xx; one that is not
      // only when the request cannot have been processed (offline, or 429).
      const retryable = idempotent
        ? isNetwork || error.code === 429 || error.code >= 500
        : !navigator.onLine || error.code === 429;

      // If it's retryable, calculate backoff
      if (retryable) {
        const backoffDelay = getBackoffDelay(retryCount);
        const retryMsg = getRetryMessage(retryCount + 1, maxRetries, backoffDelay, t);

        onRetry?.({
          error,
          retryCount: retryCount + 1,
          maxRetries,
          message: retryMsg,
          nextDelay: backoffDelay,
          isNetworkError: isNetwork
        });

        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        continue;
      }

      // Non-retryable error (4xx client errors, or a failure that may have been processed), throw immediately
      throw error;
    }
  }

  throw lastError;
}

/**
 * Validate form fields
 */
export function validateFormField(name, value, rules = {}) {
  const errors = [];

  if (rules.required && !value) {
    errors.push(`${rules.label || name} is required`);
  }

  if (value && rules.minLength && value.length < rules.minLength) {
    errors.push(`${rules.label || name} must be at least ${rules.minLength} characters`);
  }

  if (value && rules.maxLength && value.length > rules.maxLength) {
    errors.push(`${rules.label || name} cannot exceed ${rules.maxLength} characters`);
  }

  if (value && rules.pattern && !rules.pattern.test(value)) {
    errors.push(rules.patternMessage || `${rules.label || name} is invalid`);
  }

  if (value && rules.validate) {
    const customError = rules.validate(value);
    if (customError) errors.push(customError);
  }

  return errors;
}

/**
 * Validate entire form
 */
export function validateForm(formData, validationRules) {
  const formErrors = {};
  let isValid = true;

  Object.entries(validationRules).forEach(([fieldName, rules]) => {
    const fieldValue = formData[fieldName];
    const fieldErrors = validateFormField(fieldName, fieldValue, rules);

    if (fieldErrors.length > 0) {
      formErrors[fieldName] = fieldErrors[0]; // Store first error
      isValid = false;
    }
  });

  return { isValid, errors: formErrors };
}

/**
 * Check if form has validation errors
 */
export function hasValidationErrors(errors) {
  return Object.values(errors || {}).some(error => error);
}
